// Autosave for the skin editor: the full layered document in localStorage. Pure apart from the
// optional `Storage` passed in — and every storage call is wrapped, because storage can be disabled,
// full, or throw on access (private windows, blocked site data).
//
// FORMAT: a versioned KEY and a `v` field. A future format gets a new key (and a new `v`); a payload
// this code does not understand is IGNORED (returns null), never trusted and never a crash.

import type { ModelType, LayerViewMode } from './types';
import { type SkinDocument, type SkinLayer, SKIN_BYTES, MAX_LAYERS, MAX_LAYER_NAME } from './layers';

export const AUTOSAVE_KEY = 'mcwt:skin-editor:autosave:v1';
export const AUTOSAVE_VERSION = 1;
export const AUTOSAVE_DEBOUNCE_MS = 1000;

export interface AutosaveState {
  doc: SkinDocument;
  modelType: ModelType;
  layerViewMode: LayerViewMode;
  /** ms since epoch */
  savedAt: number;
}

interface StoredLayerV1 {
  id: string;
  name: string;
  visible: boolean;
  opacity: number;
  locked: boolean;
  /** base64 of the raw 16,384 RGBA bytes */
  px: string;
}

interface StoredV1 {
  v: 1;
  savedAt: number;
  modelType: ModelType;
  layerViewMode: LayerViewMode;
  activeLayerId: string;
  nextLayerNum: number;
  layers: StoredLayerV1[];
}

// ---------------------------------------------------------------------------------------------
// Byte <-> base64 (binary data, so NOT app/lib/base64.ts, which is for unicode TEXT).
// ---------------------------------------------------------------------------------------------

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const B64_INDEX = (() => {
  const t = new Int16Array(128).fill(-1);
  for (let i = 0; i < B64.length; i++) t[B64.charCodeAt(i)] = i;
  return t;
})();

export function bytesToBase64(bytes: Uint8Array | Uint8ClampedArray): string {
  let out = '';
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const n = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
    out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63] + B64[(n >> 6) & 63] + B64[n & 63];
  }
  const rem = bytes.length - i;
  if (rem === 1) {
    const n = bytes[i] << 16;
    out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63] + '==';
  } else if (rem === 2) {
    const n = (bytes[i] << 16) | (bytes[i + 1] << 8);
    out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63] + B64[(n >> 6) & 63] + '=';
  }
  return out;
}

/** Decodes strict base64. Returns null on any invalid character or length. */
export function base64ToBytes(s: string): Uint8ClampedArray | null {
  if (typeof s !== 'string' || s.length % 4 !== 0) return null;
  const pad = s.endsWith('==') ? 2 : s.endsWith('=') ? 1 : 0;
  const out = new Uint8ClampedArray((s.length / 4) * 3 - pad);
  let o = 0;
  for (let i = 0; i < s.length; i += 4) {
    const c = [0, 1, 2, 3].map(k => {
      const ch = s.charCodeAt(i + k);
      if (ch === 61 /* = */ && i + k >= s.length - pad) return 0;
      return ch < 128 ? B64_INDEX[ch] : -1;
    });
    if (c.some(v => v < 0)) return null;
    const n = (c[0] << 18) | (c[1] << 12) | (c[2] << 6) | c[3];
    if (o < out.length) out[o++] = (n >> 16) & 255;
    if (o < out.length) out[o++] = (n >> 8) & 255;
    if (o < out.length) out[o++] = n & 255;
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Serialize / deserialize
// ---------------------------------------------------------------------------------------------

export function serializeAutosave(state: AutosaveState): string {
  const stored: StoredV1 = {
    v: AUTOSAVE_VERSION,
    savedAt: state.savedAt,
    modelType: state.modelType,
    layerViewMode: state.layerViewMode,
    activeLayerId: state.doc.activeLayerId,
    nextLayerNum: state.doc.nextLayerNum,
    layers: state.doc.layers.map(l => ({
      id: l.id, name: l.name, visible: l.visible, opacity: l.opacity, locked: l.locked, px: bytesToBase64(l.pixels),
    })),
  };
  return JSON.stringify(stored);
}

/**
 * Parse a stored autosave. Returns null for anything that is not a complete, valid v1 payload —
 * unknown version, bad JSON, wrong pixel length, duplicate ids, too many layers.
 */
export function deserializeAutosave(raw: string | null | undefined): AutosaveState | null {
  if (!raw) return null;
  let data: unknown;
  try { data = JSON.parse(raw); } catch { return null; }
  if (!data || typeof data !== 'object') return null;
  const d = data as Partial<StoredV1>;
  if (d.v !== AUTOSAVE_VERSION) return null;
  if (!Array.isArray(d.layers) || d.layers.length < 1 || d.layers.length > MAX_LAYERS) return null;
  const layers: SkinLayer[] = [];
  const ids = new Set<string>();
  let maxNum = 0;
  for (const raw of d.layers) {
    if (!raw || typeof raw !== 'object') return null;
    const l = raw as Partial<StoredLayerV1>;
    if (typeof l.id !== 'string' || !l.id || ids.has(l.id)) return null;
    const pixels = typeof l.px === 'string' ? base64ToBytes(l.px) : null;
    if (!pixels || pixels.length !== SKIN_BYTES) return null;
    ids.add(l.id);
    const m = /^L(\d+)$/.exec(l.id);
    if (m) maxNum = Math.max(maxNum, Number(m[1]));
    const opacity = typeof l.opacity === 'number' && Number.isFinite(l.opacity) ? Math.min(1, Math.max(0, l.opacity)) : 1;
    layers.push({
      id: l.id,
      name: (typeof l.name === 'string' && l.name.trim() ? l.name.trim() : 'Layer').slice(0, MAX_LAYER_NAME),
      visible: l.visible !== false,
      opacity,
      locked: l.locked === true,
      pixels,
    });
  }
  const activeLayerId = typeof d.activeLayerId === 'string' && ids.has(d.activeLayerId)
    ? d.activeLayerId
    : layers[layers.length - 1].id;
  const nextLayerNum = Math.max(
    typeof d.nextLayerNum === 'number' && Number.isInteger(d.nextLayerNum) ? d.nextLayerNum : 0,
    maxNum + 1,
    layers.length + 1,
  );
  return {
    doc: { layers, activeLayerId, nextLayerNum },
    modelType: d.modelType === 'slim' ? 'slim' : 'classic',
    layerViewMode: d.layerViewMode === 'base' || d.layerViewMode === 'overlay' ? d.layerViewMode : 'both',
    savedAt: typeof d.savedAt === 'number' && Number.isFinite(d.savedAt) ? d.savedAt : 0,
  };
}

// ---------------------------------------------------------------------------------------------
// Storage wrappers — never throw.
// ---------------------------------------------------------------------------------------------

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function defaultStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null; // accessing localStorage itself throws when site data is blocked
  }
}

/** Write the autosave. Returns false on ANY failure (no storage, quota exceeded, …). */
export function writeAutosave(state: AutosaveState, storage: StorageLike | null = defaultStorage()): boolean {
  if (!storage) return false;
  try {
    storage.setItem(AUTOSAVE_KEY, serializeAutosave(state));
    return true;
  } catch {
    return false;
  }
}

export function readAutosave(storage: StorageLike | null = defaultStorage()): AutosaveState | null {
  if (!storage) return null;
  try {
    return deserializeAutosave(storage.getItem(AUTOSAVE_KEY));
  } catch {
    return null;
  }
}

export function clearAutosave(storage: StorageLike | null = defaultStorage()): void {
  try { storage?.removeItem(AUTOSAVE_KEY); } catch { /* storage unavailable — nothing to clear */ }
}
